import { GetServerSideProps, NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import SEO from '@components/shared/seo'
import { RichSnippetsHome } from '@components/home/richSnippetsHome'
import { CalculatorSolarProvider } from 'context/CalculatorSolarContext'
import { SimulationBanner } from '@components/v2/SimulationBanner'
import { useWindowDimensions } from '@hooks/use-window-dimensions'
import { Article } from 'types/article'
import { Menu, MenusApi } from '@services/api/menus'
import { ArticlesApi } from '@services/api/articles'
import { LayoutHome } from '@components/layout-home'
import { SponsorBanner } from 'components/sponsor-banner'
import { BlockCall } from 'types/block-call'
import { SystemsSoldSection } from '@components/home/systems-sold-section'
import { OurGroupCarousel } from '@components/home/our-group-carousel'
import { ArticlesTabs } from '@components/articles-tabs'
import { LatestNewsSection } from '@components/latest-news-section'
import { SpecialistsSection } from '@components/home/specialists-section'

import NewscastsService, { Newscast } from '@services/api/newscasts'

import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'

import restFetch from '@services/rest-fetch'
import { OurProductsCarousel } from '@components/home/our-products-carousel'
// TODO: criar rota de api - API para especialistas com foto, nome e artigos
interface Specialist {
  id: number
  slug: string
  title: string
  editorial: { name: string } | string
  short_content: string
  published_at: string
  time_to_read: number
  thumbnail_image: { path: string; alt: string }
  author: { name: string; photo: string; slug: string }
  path: string
}

// TODO: criar rota de api - API para empresas do grupo
interface GroupCompany {
  id: number
  name: string
  logo: string
  description: string
  website: string
}

// TODO: criar rota de api - API para cursos
interface Course {
  id: number
  title: string
  description: string
  thumbnail: string
  studentCount: number
}

interface IndexProps {
  main: Array<Menu>
  footer1: Array<Menu>
  footer2: Array<Menu>
  lastArticles: Array<Article>
  mostReadArticles: Array<Article>
  specialists: Array<Specialist>
  specialistsArticles: Array<Specialist> // Adicionando os dados reais da API
  groupCompanies: Array<BlockCall>
  ourProducts: Array<BlockCall>
  newscasts: Array<Newscast>
  courses: Array<BlockCall>
  ads: AdsIndexDto[]
  simulatorDesktopImages: Array<BlockCall>
  simulatorMobileImages: Array<BlockCall>
}

const Index: NextPage<IndexProps> = ({
  main,
  footer1,
  footer2,
  lastArticles,
  mostReadArticles,
  specialists,
  specialistsArticles,
  groupCompanies,
  ourProducts,
  newscasts,
  courses,
  ads,
  simulatorDesktopImages,
  simulatorMobileImages
}) => {
  const { width } = useWindowDimensions()

  return (
    <LayoutHome
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      logoHeading={true}
      ad={ads?.find(ad => ad.code === 'ad_sponsor_home_top')}
    >
      <Head>
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Portal Solar – Primeiro e maior portal de energia solar do Brasil"
        />
        <meta
          property="og:description"
          content="O Portal Solar é o primeiro e maior portal de energia solar do Brasil. Faça um orçamento grátis e descubra quanto custa para instalar energia solar."
        />
        <meta
          property="og:image"
          content="/assets/portal-solar-share-facebook.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="600" />
      </Head>

      <SEO
        title="Portal Solar – Primeiro e maior portal de energia solar do Brasil"
        description="O Portal Solar é o primeiro e maior portal de energia solar do Brasil. Faça um orçamento grátis e descubra quanto custa para instalar energia solar."
        canonicalUrl={`${
          process.env.NEXT_PUBLIC_URL_BASE || 'https://www.portalsolar.com.br'
        }/`}
        type="website"
        shouldIndexPage={true}
      />

      <RichSnippetsHome />

      {/* SEÇÃO HERO COM CALCULADORA */}

      <section className="container mx-5 px-4 mx-auto">
        <CalculatorSolarProvider>
          <SimulationBanner
            windowWidth={width}
            disableSnap={true}
            desktopImages={simulatorDesktopImages}
            mobileImages={simulatorMobileImages}
            logo={null}
            title={
              <p></p>
              // <p className="text-[35px] leading-[42px] tracking-wide font-normal text-v3-gray-600 md:text-[40px] md:leading-[42px] lg:text-[40px] lg:leading-[60px] xl:text-[50px] mt-[120px] md:mt-5 lg:mt-[2.25rem]">
              //   A Marca{' '}
              //   <span className="text-orange font-bold italic">Líder</span> <br />{' '}
              //   em Energia Solar
              // </p>
            }
            headingLevel="p"
          />
        </CalculatorSolarProvider>
      </section>

      {/* SEÇÃO PROPAGANDA AZUL - Removido pois já é renderizado no LayoutHome */}

      {/* SEÇÃO ÚLTIMAS NOTÍCIAS */}
      <LatestNewsSection
        articlesCount={12}
        initialArticles={lastArticles}
        className="mx-auto"
      />

      {/* SEÇÃO PROPAGANDA AZUL 2 */}
      <SponsorBanner
        className="mt-10"
        config={ads?.find(ad => ad.code === 'ad_sponsor_home_middle')}
      />

      {/* SEÇÃO DESTAQUE DOS ESPECIALISTAS */}
      <SpecialistsSection
        title="Destaque dos Especialistas"
        link="/noticias/articulistas"
        textLink="Ver mais"
        qtyArticles={4}
        specialistsArticles={specialistsArticles}
      />

      {/* INÍCIO SEÇÃO SISTEMAS VENDIDOS */}

      <section className="container mx-auto px-4 py-2 md:py-8">
        <section id="systems-sold">
          <SystemsSoldSection data={courses} />
        </section>
      </section>
      {/* SEÇÃO PROPAGANDA AZUL 3 */}
      <SponsorBanner
        className="mt-10"
        config={ads?.find(ad => ad.code === 'ad_sponsor_home_bottom')}
      />

      {/* SEÇÃO NOSSO GRUPO */}
      <OurGroupCarousel groupCompanies={groupCompanies} />

      {/* SEÇÃO NOSSOS PRODUTOS */}
      <section className="container mx-auto px-4 py-2 md:py-8">
        <OurProductsCarousel ourProducts={ourProducts} />
      </section>
      {/* SEÇÃO FINAL COM ARTIGOS */}
      <ArticlesTabs
        articlesPerPage={9}
        showGuideTab={true}
        initialLastArticles={lastArticles}
        initialMostReadArticles={mostReadArticles}
        initialNewscasts={newscasts}
      />
    </LayoutHome>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  try {
    const [
      menus,
      {
        data: { articles: lastArticles }
      },
      {
        data: { articles: mostReadArticles }
      },
      newscastsResponse
    ] = await Promise.all([
      MenusApi.all(),
      ArticlesApi.index({ per: 12 }),
      ArticlesApi.index({ per: 9, more_reads: true }),
      NewscastsService.index({ size: 9 })
    ])

    const newscasts = newscastsResponse.data

    const coursesResponse = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=home_solar_courses')
    const courses = coursesResponse?.blockCalls || []

    // criar novos no banco de dados expansion_lp_project e residential
    const groupCompaniesResponse = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=home_solar_group')
    const groupCompanies = groupCompaniesResponse?.blockCalls || []

    const ourProductsResponse = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=home_solar_products')
    const ourProducts = ourProductsResponse?.blockCalls || []

    // Buscar imagens do simulador
    const simulatorDesktopResponse = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=simulator')
    const simulatorDesktopImages = simulatorDesktopResponse?.blockCalls || []

    const simulatorMobileResponse = await restFetch<{
      blockCalls: BlockCall[]
    }>('/block_calls?block_type=simulator_image_mobile')
    const simulatorMobileImages = simulatorMobileResponse?.blockCalls || []

    // Buscar destaques dos especialistas via API
    let specialistsArticles = []
    try {
      const {
        data: { articles: specialistsData }
      } = await ArticlesApi.specialistsHighlights()
      specialistsArticles = specialistsData
    } catch (error) {}

    // Usar dados da API se disponíveis, senão usar mockados
    const specialists =
      specialistsArticles.length > 0 ? specialistsArticles : []

    const {
      data: { ads }
    } = await AdsApi.index({
      codes: [
        'ad_sponsor_home_top',
        'ad_sponsor_home_middle',
        'ad_sponsor_home_bottom'
      ]
    })

    return {
      props: {
        main: menus.main,
        footer1: menus.footer1,
        footer2: menus.footer2,
        ads: ads || null,
        lastArticles,
        mostReadArticles,
        specialists,
        specialistsArticles, // Adicionando os dados reais da API
        groupCompanies,
        ourProducts,
        newscasts,
        courses,
        simulatorDesktopImages,
        simulatorMobileImages
      }
    }
  } catch (error) {
    console.error('Error fetching home data:', error)
    return {
      props: {
        main: [],
        footer1: [],
        footer2: [],
        ads: [],
        lastArticles: [],
        mostReadArticles: [],
        specialists: [],
        specialistsArticles: [],
        groupCompanies: [],
        ourProducts: [],
        courses: [],
        newscasts: [],
        simulatorDesktopImages: [],
        simulatorMobileImages: []
      }
    }
  }
}

export default Index
